import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { format } from "date-fns";
import {
  CheckCircle2, XCircle, BellOff, Loader2, Send, ShieldCheck, ExternalLink,
} from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { ScrollArea } from "@/components/ui/scroll-area";
import { SIGNALS } from "@/constants/testIds";
import { SEVERITY } from "@/lib/alerts";
import { SOURCE_LABEL, STATUS, fmtNum } from "@/lib/signals";
import api from "@/lib/api";

function abs(ts) {
  try { return format(new Date(ts), "MMM d, yyyy · HH:mm"); } catch { return "—"; }
}

export function SignalEvidenceDialog({ signalId, open, onOpenChange, onChanged, canReview }) {
  const [signal, setSignal] = useState(null);
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState(false);
  const [commenting, setCommenting] = useState(false);

  const load = useCallback(async () => {
    if (!signalId) return;
    const { data } = await api.get(`/signals/${signalId}`);
    setSignal(data);
  }, [signalId]);

  useEffect(() => {
    if (open && signalId) {
      setSignal(null);
      setComment("");
      load();
    }
  }, [open, signalId, load]);

  const act = async (verb) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/signals/${signalId}/${verb}`);
      setSignal(data);
      toast.success(`Signal ${data.status}`);
      onChanged?.();
    } catch (e) {
      toast.error("Action failed", { description: e.response?.data?.detail || e.message });
    } finally {
      setBusy(false);
    }
  };

  const addComment = async () => {
    if (!comment.trim()) return;
    setCommenting(true);
    try {
      const { data } = await api.post(`/signals/${signalId}/comment`, { body: comment.trim() });
      setSignal(data);
      setComment("");
      onChanged?.();
    } catch (e) {
      toast.error("Couldn't post comment", { description: e.response?.data?.detail || e.message });
    } finally {
      setCommenting(false);
    }
  };

  const sev = signal && (SEVERITY[signal.severity] || SEVERITY.low);
  const st = signal && (STATUS[signal.status] || STATUS.pending);
  const ev = signal?.evidence_frozen || {};
  const isPending = signal?.status === "pending";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid={SIGNALS.dialog} className="max-w-2xl font-mono">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base">
            {signal?.name || "Signal"}
            {sev && <Badge variant="outline" className={`text-[10px] ${sev.cls}`}>{sev.label}</Badge>}
            {st && <Badge variant="outline" className={`text-[10px] ${st.cls}`}>{st.label}</Badge>}
          </DialogTitle>
          <DialogDescription className="text-xs">
            {signal ? (
              <>
                {SOURCE_LABEL[signal.source] || signal.source} · detected {abs(signal.detected_at)}
                {signal.reviewer_name && ` · reviewed by ${signal.reviewer_name}`}
              </>
            ) : "Loading evidence…"}
          </DialogDescription>
        </DialogHeader>

        {!signal ? (
          <div className="py-12 text-center text-sm text-muted-foreground">
            <Loader2 className="mx-auto h-5 w-5 animate-spin" />
          </div>
        ) : (
          <ScrollArea className="max-h-[60vh] pr-3">
            {signal.summary && <p className="text-sm text-foreground/90">{signal.summary}</p>}

            {ev.chart_png_b64 ? (
              <img src={`data:image/png;base64,${ev.chart_png_b64}`} alt="evidence"
                className="mt-3 w-full rounded-md border border-border/50" />
            ) : (
              <div className="mt-3 rounded-md border border-dashed border-border/50 px-3 py-8 text-center text-xs text-muted-foreground">
                No chart evidence
              </div>
            )}

            <div className="mt-3 grid grid-cols-3 gap-2 text-xs">
              <div className="rounded-lg border border-border/50 bg-card/40 px-3 py-2">
                <div className="text-[10px] uppercase tracking-[0.12em] text-muted-foreground">Value</div>
                <div className="mt-0.5 text-foreground">{fmtNum(ev.value)}</div>
              </div>
              <div className="rounded-lg border border-border/50 bg-card/40 px-3 py-2">
                <div className="text-[10px] uppercase tracking-[0.12em] text-muted-foreground">Baseline</div>
                <div className="mt-0.5 text-foreground">{fmtNum(ev.baseline)}</div>
              </div>
              <div className="rounded-lg border border-border/50 bg-card/40 px-3 py-2">
                <div className="text-[10px] uppercase tracking-[0.12em] text-muted-foreground">Deviation</div>
                <div className="mt-0.5 text-foreground">{fmtNum(ev.deviation)}</div>
              </div>
            </div>

            {ev.sql && (
              <pre className="mt-3 overflow-auto rounded-lg border border-border/50 bg-background/60 p-3 text-[11px] text-muted-foreground">{ev.sql}</pre>
            )}

            {signal.registry_id && (
              <Link to={`/signals/registry/${signal.registry_id}`}
                className="mt-3 inline-flex items-center gap-1.5 text-xs text-emerald-400 hover:underline">
                <ShieldCheck className="h-3.5 w-3.5" /> View immutable registry record
                <ExternalLink className="h-3 w-3" />
              </Link>
            )}

            {/* Comment thread */}
            <div className="mt-5">
              <div className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground">Review thread</div>
              <div className="mt-2 space-y-2">
                {(signal.comments || []).length === 0 && (
                  <p className="text-xs text-muted-foreground">No comments yet.</p>
                )}
                {(signal.comments || []).map((c, i) => (
                  <div key={i} data-testid={SIGNALS.commentItem} className="rounded-lg border border-border/40 bg-card/30 px-3 py-2">
                    <div className="flex items-center justify-between text-[11px]">
                      <span className="font-medium text-foreground/90">{c.author_name}</span>
                      <span className="text-muted-foreground">{abs(c.created_at)}</span>
                    </div>
                    <p className="mt-1 text-xs text-foreground/80">{c.body}</p>
                  </div>
                ))}
              </div>
              <div className="mt-2 flex items-end gap-2">
                <Textarea data-testid={SIGNALS.commentInput} value={comment} rows={1}
                  onChange={(e) => setComment(e.target.value)}
                  placeholder="Add a note for the record…" className="min-h-[40px] resize-none bg-card/60 text-sm" />
                <Button data-testid={SIGNALS.commentSubmit} size="sm" onClick={addComment}
                  disabled={commenting || !comment.trim()} className="h-10 rounded-full bg-muted text-foreground hover:bg-muted/70">
                  {commenting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                </Button>
              </div>
            </div>
          </ScrollArea>
        )}

        {signal && canReview && isPending && (
          <div className="flex flex-wrap items-center justify-end gap-2 border-t border-border/50 pt-3">
            <Button data-testid={SIGNALS.dialogSilence} variant="outline" size="sm" onClick={() => act("silence")} disabled={busy}
              className="rounded-full">
              <BellOff className="mr-1.5 h-4 w-4" /> Silence
            </Button>
            <Button data-testid={SIGNALS.dialogReject} variant="outline" size="sm" onClick={() => act("reject")} disabled={busy}
              className="rounded-full border-red-500/30 text-red-400 hover:bg-red-500/10">
              <XCircle className="mr-1.5 h-4 w-4" /> Reject
            </Button>
            <Button data-testid={SIGNALS.dialogApprove} size="sm" onClick={() => act("approve")} disabled={busy}
              className="rounded-full bg-emerald-500 text-white hover:bg-emerald-600">
              {busy ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <CheckCircle2 className="mr-1.5 h-4 w-4" />}
              Approve to registry
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
